// Shared pieces for the FWAA Business Exchange board: role badges,
// claim controls, the claim-board summary, and the canonical property
// table (Property / City / Property Manager / Regional at every level).

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { formatDateTime } from '../../utils/dateUtils';
import { usePhoneLimit, ShowMore } from '../../mobile';

export function RoleBadge({ roleType, title }) {
  const map = {
    regional: { label: 'REGIONAL', cls: 'fwaa-badge--regional' },
    property_manager: { label: 'PROPERTY MANAGER', cls: 'fwaa-badge--pm' },
    other: { label: 'STAFF', cls: 'fwaa-badge--staff' },
  };
  const m = map[roleType] || map.other;
  return <span className={`fwaa-badge ${m.cls}`} title={title || ''}>{m.label}</span>;
}

export function ApolloPill() {
  return <span className="fwaa-apollo-pill">APOLLO</span>;
}

// Claim button / chip. Renders based on who (if anyone) holds the claim.
// onChanged(freshBoard) fires after a successful claim/release so the
// parent can refresh its data.
export function ClaimControl({ attendee, myUserId, onChanged, size = 'sm' }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const claimedBy = attendee.claimed_by;
  const mine = claimedBy === myUserId;

  async function claim(e) {
    e.preventDefault(); e.stopPropagation();
    setBusy(true); setError('');
    try {
      await api.claimFwaaAttendee(attendee.id);
      onChanged && onChanged();
    } catch (err) {
      setError(err?.responseBody?.error || err.message || 'Claim failed');
      onChanged && onChanged(); // someone may have beaten us — refresh
    } finally { setBusy(false); }
  }

  async function release(e) {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm(`Release your appointment with ${attendee.name}?`)) return;
    setBusy(true); setError('');
    try {
      await api.unclaimFwaaAttendee(attendee.id);
      onChanged && onChanged();
    } catch (err) {
      setError(err?.responseBody?.error || err.message || 'Release failed');
    } finally { setBusy(false); }
  }

  if (!claimedBy) {
    return (
      <span className="fwaa-claim-wrap">
        <button type="button" className={`fwaa-claim-btn fwaa-claim-btn--${size}`} onClick={claim} disabled={busy}>
          {busy ? '...' : 'Claim'}
        </button>
        {error ? <span className="fwaa-claim-error">{error}</span> : null}
      </span>
    );
  }
  if (mine) {
    return (
      <span className="fwaa-claim-wrap">
        <span className="fwaa-claim-chip fwaa-claim-chip--mine">
          ✓ Yours
          <button type="button" className="fwaa-claim-release" onClick={release} disabled={busy} title="Release this appointment">✕</button>
        </span>
        {error ? <span className="fwaa-claim-error">{error}</span> : null}
      </span>
    );
  }
  return <span className="fwaa-claim-chip">{attendee.claimed_by_name || 'Claimed'}</span>;
}

// Mark-completed toggle for a claim the current user owns (or admin).
// Completed appointments STAY on the list — this only flips status.
export function CompleteToggle({ attendeeId, status, onChanged, size = 'sm' }) {
  const [busy, setBusy] = useState(false);
  const completed = status === 'completed';

  async function flip(e) {
    e.preventDefault(); e.stopPropagation();
    setBusy(true);
    try {
      await api.setFwaaClaimCompleted(attendeeId, !completed);
      onChanged && onChanged();
    } catch (err) {
      alert(err?.responseBody?.error || err.message || 'Update failed');
    } finally { setBusy(false); }
  }

  if (completed) {
    return (
      <button type="button" className="fwaa-done-chip" onClick={flip} disabled={busy}
        title="Click to move back to active">
        ✓ Completed
      </button>
    );
  }
  return (
    <button type="button" className={`fwaa-complete-btn fwaa-complete-btn--${size}`} onClick={flip} disabled={busy}>
      {busy ? '...' : 'Mark Completed'}
    </button>
  );
}

// Team-visible notes thread on an attendee. Add box always at the
// bottom; delete on your own notes (admins any — server enforces).
export function NotesThread({ attendeeId, notes, myUserId, onChanged, placeholder }) {
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function add(e) {
    e.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      await api.addFwaaNote(attendeeId, body);
      setDraft('');
      onChanged && onChanged();
    } catch (err) {
      alert(err?.responseBody?.error || err.message || 'Failed to add note');
    } finally { setBusy(false); }
  }

  async function remove(noteId) {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.deleteFwaaNote(noteId);
      onChanged && onChanged();
    } catch (err) {
      alert(err?.responseBody?.error || err.message || 'Failed to delete note');
    }
  }

  return (
    <div className="fwaa-notes">
      {(notes || []).length === 0 ? (
        <div className="fwaa-notes__empty">No notes yet.</div>
      ) : (
        <ul className="fwaa-notes__list">
          {notes.map(n => (
            <li key={n.id} className="fwaa-note">
              <div className="fwaa-note__head">
                <span className="fwaa-note__author">{n.user_name}</span>
                <span className="fwaa-note__time">{formatDateTime(n.created_at)}</span>
                {n.user_id === myUserId ? (
                  <button type="button" className="fwaa-note__delete" onClick={() => remove(n.id)} title="Delete note">✕</button>
                ) : null}
              </div>
              <div className="fwaa-note__body">{n.body}</div>
            </li>
          ))}
        </ul>
      )}
      <form className="fwaa-notes__add" onSubmit={add}>
        <input
          type="text"
          className="fwaa-notes__input"
          placeholder={placeholder || 'Add a note — what you talked about, next steps...'}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          maxLength={2000}
        />
        <button type="submit" className="fwaa-notes__save" disabled={busy || !draft.trim()}>
          {busy ? '...' : 'Add Note'}
        </button>
      </form>
    </div>
  );
}

// "Your appointments 3/15 · Team 11 · Open regionals 23" strip.
export function ClaimSummary({ board }) {
  if (!board) return null;
  return (
    <div className="fwaa-claim-summary">
      <span className="fwaa-claim-summary__mine">
        Your appointments: <strong>{board.my_count}</strong> / {board.limit}
      </span>
      <span className="fwaa-claim-summary__sep">·</span>
      <span>Team claimed: <strong>{board.total_claimed}</strong></span>
    </div>
  );
}

// Who-claimed-who board, grouped by rep.
export function ClaimBoard({ board, myUserId, onChanged }) {
  const groups = useMemo(() => {
    const by = new Map();
    for (const cl of (board?.claims || [])) {
      if (!by.has(cl.claimed_by)) by.set(cl.claimed_by, { user_name: cl.user_name, user_id: cl.claimed_by, claims: [] });
      by.get(cl.claimed_by).claims.push(cl);
    }
    return [...by.values()].sort((a, b) => b.claims.length - a.claims.length);
  }, [board]);

  if (!board || board.claims.length === 0) {
    return <div className="fwaa-claimboard-empty">No appointments claimed yet — the board is wide open.</div>;
  }
  return (
    <div className="fwaa-claimboard">
      {groups.map(g => (
        <div key={g.user_id} className="fwaa-claimboard__rep">
          <div className="fwaa-claimboard__rep-head">
            <span className="fwaa-claimboard__rep-name">{g.user_name}</span>
            <span className="fwaa-claimboard__rep-count">{g.claims.length} / {board.limit}</span>
          </div>
          <ul className="fwaa-claimboard__list">
            {g.claims.map(cl => (
              <li key={cl.attendee_id} className="fwaa-claimboard__item">
                <span className="fwaa-claimboard__attendee">
                  {cl.status === 'completed' ? <span className="fwaa-claimboard__done" title="Appointment completed">✓ </span> : null}
                  {cl.attendee_name}
                </span>
                <span className="fwaa-claimboard__company">{cl.company_name}</span>
                {cl.attendee_title ? <span className="fwaa-claimboard__title">{cl.attendee_title}</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

// The canonical property table. Same 4 lead columns at every level:
// Property Name / City / Property Manager / Regional (Area Supervisor).
export function FwaaPropertyTable({ properties, highlightAttendeeId = null, showRegionalCol = true }) {
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return properties;
    return properties.filter(p =>
      [p.name, p.city, p.manager_name, p.regional_name, p.tow_rep, p.apollo_account_name]
        .some(v => (v || '').toLowerCase().includes(needle))
    );
  }, [properties, q]);
  // Big books on a phone: first 40 property cards + Show more.
  const { shown, rest, more } = usePhoneLimit(visible, visible);

  return (
    <div className="fwaa-proptable">
      <div className="fwaa-proptable__bar">
        <input
          type="search"
          className="fwaa-proptable__filter"
          placeholder="Filter properties, cities, managers, regionals..."
          value={q}
          onChange={e => setQ(e.target.value)}
        />
        <span className="fwaa-proptable__count">{visible.length} of {properties.length}</span>
      </div>
      <div className="fwaa-table-wrap">
        <table className="fwaa-table m-cards">
          <thead>
            <tr>
              <th>Property</th>
              <th>City</th>
              <th>Property Manager</th>
              {showRegionalCol ? <th>Regional (Area Supervisor)</th> : null}
              <th>Units</th>
              <th>Apollo</th>
              <th>Tow Rep</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(p => {
              const attending = p.regional_attendee_id || p.pm_attendee_id;
              const hot = highlightAttendeeId
                ? (p.regional_attendee_id === highlightAttendeeId || p.pm_attendee_id === highlightAttendeeId)
                : attending;
              const linkId = p.regional_attendee_id || p.pm_attendee_id;
              return (
                <tr
                  key={p.id}
                  className={hot ? 'fwaa-table__row--attending' : ''}
                  onClick={linkId && !highlightAttendeeId ? () => navigate(`/sales/fwaa/attendees/${linkId}`) : undefined}
                  style={linkId && !highlightAttendeeId ? { cursor: 'pointer' } : undefined}
                  title={linkId && !highlightAttendeeId ? 'Open the attending contact for this property' : undefined}
                >
                  <td className="fwaa-table__name">{p.name}</td>
                  <td>{p.city || '—'}</td>
                  <td>{p.manager_name || '—'}</td>
                  {showRegionalCol ? (
                    <td>
                      {p.regional_name || '—'}
                      {p.regional_attendee_id && !highlightAttendeeId ? <span className="fwaa-here-dot" title="Attending FWAA"> ●</span> : null}
                    </td>
                  ) : null}
                  <td>{p.units || '—'}</td>
                  <td>
                    {p.apollo_customer ? <ApolloPill /> : <span className="fwaa-muted">—</span>}
                    {p.apollo_customer && p.apollo_account_name && p.apollo_account_name !== p.name
                      ? <div className="fwaa-muted fwaa-small">{p.apollo_account_name}</div> : null}
                  </td>
                  <td>{p.tow_rep || '—'}</td>
                </tr>
              );
            })}
            {visible.length === 0 ? (
              <tr><td colSpan={showRegionalCol ? 7 : 6} className="fwaa-empty-cell">No properties match.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <ShowMore rest={rest} onMore={more} />
    </div>
  );
}
